import React from 'react';
import PlaceDetailsPanel from './PlaceDetailsPanel';
import MapPlaceOverlay from './MapPlaceOverlay';
import {
  PlaceDetails,
} from './placeDetails';
import {
  fetchPlaceDetailsFromGoogle,
  getPlaceLocation,
} from './googlePlacesService';
import './MapPlaceSidebar.css';

export interface TripMapActivity {
  id: string;
  day: string;
  description: string;
  place?: string;
}

interface TripMapSidebarProps {
  destinations: string[];
  activities: TripMapActivity[];
  selectedDay: string;
  selectedActivityId: string | null;
  onSelectActivity: (activityId: string) => void;
  onClearPlace?: () => void;
  mapOnly?: boolean;
  className?: string;
  selectedExternalPlaceId?: string | null;
  selectedExternalPlaceQuery?: string;
}

interface ActivityMarkerRecord {
  activityId: string;
  marker: any;
  placeId?: string;
}

const TripMapSidebar: React.FC<TripMapSidebarProps> = ({
  destinations,
  activities,
  selectedDay,
  selectedActivityId,
  onSelectActivity,
  onClearPlace,
  mapOnly = false,
  className,
  selectedExternalPlaceId = null,
  selectedExternalPlaceQuery,
}) => {
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const activityMarkersRef = React.useRef<ActivityMarkerRecord[]>([]);
  const activityPlaceIdsRef = React.useRef<Map<string, string>>(new Map());
  const pendingPlaceRequestRef = React.useRef(0);

  const [mapType, setMapType] = React.useState<'roadmap' | 'satellite'>('roadmap');
  const [mapReady, setMapReady] = React.useState(false);
  const [selectedPlace, setSelectedPlace] = React.useState<PlaceDetails | null>(null);
  const [isLoadingPlace, setIsLoadingPlace] = React.useState(false);
  const [mappedActivitiesCount, setMappedActivitiesCount] = React.useState(0);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasApiKey = Boolean(googleMapsApiKey);

  const dayActivities = React.useMemo(
    () => activities.filter((activity) => activity.day === selectedDay),
    [activities, selectedDay]
  );

  const locationContext = React.useMemo(
    () => destinations.filter(Boolean).join(', '),
    [destinations]
  );

  const clearActivityMarkers = React.useCallback(() => {
    activityMarkersRef.current.forEach(({ marker }) => marker.setMap(null));
    activityMarkersRef.current = [];
    setMappedActivitiesCount(0);
  }, []);

  const fetchPlaceDetailsById = React.useCallback(async (placeId: string, queryFallback?: string) => {
    if (!mapInstanceRef.current) return;

    const requestId = pendingPlaceRequestRef.current + 1;
    pendingPlaceRequestRef.current = requestId;
    setIsLoadingPlace(true);

    const details = await fetchPlaceDetailsFromGoogle(mapInstanceRef.current, placeId, {
      queryFallback,
    });

    if (requestId !== pendingPlaceRequestRef.current) return;

    setIsLoadingPlace(false);

    if (!details) {
      setSelectedPlace(null);
      return;
    }

    setSelectedPlace(details);

    const location = getPlaceLocation(details);
    if (location && mapInstanceRef.current) {
      mapInstanceRef.current.panTo(location);
      mapInstanceRef.current.setZoom(15);
    }
  }, []);

  const resolveActivityPlaceId = React.useCallback((activity: TripMapActivity) => {
    const cachedPlaceId = activityPlaceIdsRef.current.get(activity.id);
    if (cachedPlaceId) {
      return Promise.resolve(cachedPlaceId);
    }

    if (!mapInstanceRef.current || !window.google?.maps?.places) {
      return Promise.resolve(null);
    }

    const queryBase = activity.place?.trim() || activity.description.trim();
    if (!queryBase) {
      return Promise.resolve(null);
    }

    const query = locationContext ? `${queryBase}, ${locationContext}` : queryBase;
    const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);
    const mapCenter = mapInstanceRef.current.getCenter();

    return new Promise<string | null>((resolve) => {
      service.findPlaceFromQuery(
        {
          query,
          fields: ['place_id', 'geometry', 'name'],
          locationBias: mapCenter
            ? {
                center: mapCenter,
                radius: 50000,
              }
            : undefined,
        },
        (results: Array<{ place_id?: string }> | null, status: string) => {
          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK
            || !results?.[0]?.place_id
          ) {
            resolve(null);
            return;
          }

          activityPlaceIdsRef.current.set(activity.id, results[0].place_id!);
          resolve(results[0].place_id!);
        }
      );
    });
  }, [locationContext]);

  const loadSelectedActivityPlace = React.useCallback(async (activityId: string | null) => {
    if (!activityId) {
      pendingPlaceRequestRef.current += 1;
      setSelectedPlace(null);
      setIsLoadingPlace(false);
      return;
    }

    const activity = activities.find((item) => item.id === activityId);
    if (!activity) {
      setSelectedPlace(null);
      return;
    }

    setIsLoadingPlace(true);
    const placeId = await resolveActivityPlaceId(activity);

    if (!placeId) {
      setIsLoadingPlace(false);
      setSelectedPlace({
        name: activity.place || activity.description,
        formatted_address: locationContext || 'Location details unavailable',
      });
      return;
    }

    fetchPlaceDetailsById(placeId);
  }, [activities, fetchPlaceDetailsById, locationContext, resolveActivityPlaceId]);

  const plotDayActivities = React.useCallback(async () => {
    if (!mapInstanceRef.current || !window.google?.maps?.Marker) return;

    clearActivityMarkers();

    if (dayActivities.length === 0) {
      if (destinations.length > 0 && window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: destinations[0] }, (results: any, status: string) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            mapInstanceRef.current.setCenter(results[0].geometry.location);
            mapInstanceRef.current.setZoom(11);
          }
        });
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let placedCount = 0;

    await Promise.all(dayActivities.map(async (activity, index) => {
      const placeId = await resolveActivityPlaceId(activity);
      if (!placeId || !mapInstanceRef.current) return;

      await new Promise<void>((resolve) => {
        const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);
        service.getDetails(
          {
            placeId,
            fields: ['geometry', 'name'],
          },
          (place: PlaceDetails | null, status: string) => {
            if (
              status !== window.google.maps.places.PlacesServiceStatus.OK
              || !place?.geometry?.location
            ) {
              resolve();
              return;
            }

            const marker = new window.google.maps.Marker({
              map: mapInstanceRef.current,
              position: place.geometry.location,
              title: activity.description,
              label: {
                text: String(index + 1),
                color: '#ffffff',
                fontWeight: '700',
              },
            });

            marker.addListener('click', () => {
              onSelectActivity(activity.id);
            });

            activityMarkersRef.current.push({
              activityId: activity.id,
              marker,
              placeId,
            });
            bounds.extend(place.geometry.location);
            placedCount += 1;
            resolve();
          }
        );
      });
    }));

    setMappedActivitiesCount(placedCount);

    if (placedCount > 1) {
      mapInstanceRef.current.fitBounds(bounds, 48);
    } else if (placedCount === 1) {
      mapInstanceRef.current.setCenter(bounds.getCenter());
      mapInstanceRef.current.setZoom(14);
    }
  }, [clearActivityMarkers, dayActivities, destinations, onSelectActivity, resolveActivityPlaceId]);

  React.useEffect(() => {
    if (!hasApiKey || !mapRef.current || mapInstanceRef.current) return undefined;

    let cancelled = false;
    let pollTimer: number | undefined;

    const initMap = () => {
      if (cancelled || !window.google?.maps?.Map || !mapRef.current || mapInstanceRef.current) return;

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 38.7223, lng: -9.1393 },
        zoom: 11,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      setMapReady(true);
    };

    if (window.google?.maps?.Map) {
      initMap();
    } else {
      pollTimer = window.setInterval(() => {
        if (window.google?.maps?.Map) {
          window.clearInterval(pollTimer);
          initMap();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }
    };
  }, [hasApiKey]);

  React.useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setMapTypeId(mapType);
  }, [mapType]);

  React.useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return undefined;

    plotDayActivities();

    return () => {
      clearActivityMarkers();
    };
  }, [clearActivityMarkers, mapReady, plotDayActivities, selectedDay]);

  React.useEffect(() => {
    if (!mapReady) return;
    if (selectedExternalPlaceId) return;
    loadSelectedActivityPlace(selectedActivityId);
  }, [loadSelectedActivityPlace, mapReady, selectedActivityId, selectedExternalPlaceId]);

  React.useEffect(() => {
    if (!mapReady || !selectedExternalPlaceId) return;
    fetchPlaceDetailsById(selectedExternalPlaceId, selectedExternalPlaceQuery);
  }, [fetchPlaceDetailsById, mapReady, selectedExternalPlaceId, selectedExternalPlaceQuery]);

  React.useEffect(() => {
    activityPlaceIdsRef.current.clear();
  }, [selectedDay, locationContext]);

  const badgeLabel = mappedActivitiesCount === 1
    ? '1 place on map'
    : `${mappedActivitiesCount} places on map`;

  const selectedActivity = React.useMemo(
    () => activities.find((activity) => activity.id === selectedActivityId) || null,
    [activities, selectedActivityId],
  );

  const showMapOverlay = mapOnly && Boolean(selectedActivityId || selectedExternalPlaceId);
  const overlayFallbackTitle = selectedActivity?.place || selectedActivity?.description || selectedExternalPlaceQuery;

  const handleCloseOverlay = () => {
    pendingPlaceRequestRef.current += 1;
    setSelectedPlace(null);
    setIsLoadingPlace(false);
    onClearPlace?.();
  };

  return (
    <div
      className={`v2-map-sidebar ${mapOnly ? 'v2-map-sidebar--compact' : ''} ${className || ''}`.trim()}
      aria-label={mapOnly ? 'Trip map' : 'Map and place details'}
    >
      <div className="v2-map-card">
        {hasApiKey ? (
          <>
            <div ref={mapRef} className="v2-map-card__canvas" />
            {showMapOverlay && <div className="v2-map-card__dim" aria-hidden />}
            {showMapOverlay && (
              <MapPlaceOverlay
                place={selectedPlace}
                isLoading={isLoadingPlace}
                fallbackTitle={overlayFallbackTitle}
                onClose={handleCloseOverlay}
              />
            )}
            <div className="v2-map-card__controls">
              <div className="v2-map-card__type">
                <button
                  type="button"
                  className={mapType === 'roadmap' ? 'v2-map-card__type--active' : undefined}
                  onClick={() => setMapType('roadmap')}
                >
                  Map
                </button>
                <button
                  type="button"
                  className={mapType === 'satellite' ? 'v2-map-card__type--active' : undefined}
                  onClick={() => setMapType('satellite')}
                >
                  Satellite
                </button>
              </div>
              <div className="v2-map-card__zoom">
                <button
                  type="button"
                  aria-label="Zoom in"
                  onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) + 1)}
                >
                  +
                </button>
                <button
                  type="button"
                  aria-label="Zoom out"
                  onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) - 1)}
                >
                  −
                </button>
              </div>
            </div>
            <div className="v2-map-card__badge">
              <span aria-hidden>📍</span>
              {badgeLabel}
            </div>
          </>
        ) : (
          <div className="v2-map-card__fallback">
            <p>Map preview unavailable</p>
            <span>Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the map.</span>
          </div>
        )}
      </div>

      {!mapOnly && (
        <PlaceDetailsPanel
          place={selectedPlace}
          isLoading={isLoadingPlace}
          emptyMessage="Select a recommended place, an activity from the day plan, or tap a numbered pin on the map to see photos, hours, and details."
        />
      )}
    </div>
  );
};

export default TripMapSidebar;
